import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Alert, Button, Card, Segmented, Space, Spin, Typography, App} from "antd";
import {SaveOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../../Contexts/AuthContext";
import {FileUploadUrlInput} from "../../Components/FileUploadUrlInput";
import {useSelectedEventWorkspace} from "./EventWorkspaceLayout";
import TemplatePlacementEditor, {A6_TEMPLATE_CANVAS, TemplatePlacement} from "./TemplatePlacementEditor";
import TemplateTextFieldList from "./TemplateTextFieldList";
import {
    createDefaultBadgeTextFields,
    isTemplateQrCodeField,
    serializeTemplateTextFields,
    TemplateTextFieldDefinition,
    toTemplatePlacement
} from "./templateTextFields";
import {
    buildEventTemplateSettingsSaveInput,
    getEventTemplateBranding
} from "./eventTemplateEditorData";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {downloadBadgeTemplatePreview} from "./templatePreviewDownload";
import {StatusTag} from "../../Components/ui/StatusTag";
import {PageHeader} from "../../Components/ui";
import {
    useEventTemplateSettingsQuery,
    useSaveEventTemplateSettingsMutation
} from "../../GraphQL/generated/graphql";

const BADGE_CANVAS = {
    width: A6_TEMPLATE_CANVAS.width,
    height: A6_TEMPLATE_CANVAS.height
};

const DEFAULT_BADGE_PLACEMENT = {
    firstName: {x: 92, y: 218},
    lastName: {x: 92, y: 264},
    company: {x: 92, y: 326},
    qrCode: {x: A6_TEMPLATE_CANVAS.width / 2, y: A6_TEMPLATE_CANVAS.height / 2}
};

type BadgeEditorSide = "front" | "back";

type BadgeTemplateState = {
    badgeTemplateUrl: string;
    badgeBackTemplateUrl: string;
    badgeFirstNameX: number;
    badgeFirstNameY: number;
    badgeLastNameX: number;
    badgeLastNameY: number;
    badgeCompanyX: number;
    badgeCompanyY: number;
    badgeQrCodeX: number;
    badgeQrCodeY: number;
    badgeTextFields: TemplateTextFieldDefinition[];
    badgeTextFieldsJson: string;
};

const textValue = (value?: string | null) => value?.trim() ?? "";

const buildBadgeState = (
    branding: ReturnType<typeof getEventTemplateBranding>
): BadgeTemplateState => {
    const state = {
        badgeTemplateUrl: textValue(branding?.badgeTemplateUrl),
        badgeBackTemplateUrl: textValue(branding?.badgeBackTemplateUrl),
        badgeFirstNameX: branding?.badgeFirstNameX ?? DEFAULT_BADGE_PLACEMENT.firstName.x,
        badgeFirstNameY: branding?.badgeFirstNameY ?? DEFAULT_BADGE_PLACEMENT.firstName.y,
        badgeLastNameX: branding?.badgeLastNameX ?? DEFAULT_BADGE_PLACEMENT.lastName.x,
        badgeLastNameY: branding?.badgeLastNameY ?? DEFAULT_BADGE_PLACEMENT.lastName.y,
        badgeCompanyX: branding?.badgeCompanyX ?? DEFAULT_BADGE_PLACEMENT.company.x,
        badgeCompanyY: branding?.badgeCompanyY ?? DEFAULT_BADGE_PLACEMENT.company.y,
        badgeQrCodeX: branding?.badgeQrCodeX ?? DEFAULT_BADGE_PLACEMENT.qrCode.x,
        badgeQrCodeY: branding?.badgeQrCodeY ?? DEFAULT_BADGE_PLACEMENT.qrCode.y,
        badgeTextFieldsJson: textValue(branding?.badgeTextFieldsJson)
    };

    const badgeTextFields = createDefaultBadgeTextFields({
        ...state,
        badgeTextFieldsJson: state.badgeTextFieldsJson
    });

    return {
        ...state,
        badgeTextFields
    };
};

const syncLegacyBadgeCoordinates = (state: BadgeTemplateState): BadgeTemplateState => {
    const findField = (id: string) => state.badgeTextFields.find(field => field.id === id);
    const firstName = findField("firstName");
    const lastName = findField("lastName");
    const company = findField("company");
    const qrCode = state.badgeTextFields.find(isTemplateQrCodeField);

    return {
        ...state,
        badgeFirstNameX: firstName?.x ?? state.badgeFirstNameX,
        badgeFirstNameY: firstName?.y ?? state.badgeFirstNameY,
        badgeLastNameX: lastName?.x ?? state.badgeLastNameX,
        badgeLastNameY: lastName?.y ?? state.badgeLastNameY,
        badgeCompanyX: company?.x ?? state.badgeCompanyX,
        badgeCompanyY: company?.y ?? state.badgeCompanyY,
        badgeQrCodeX: qrCode?.x ?? state.badgeQrCodeX,
        badgeQrCodeY: qrCode?.y ?? state.badgeQrCodeY,
        badgeTextFieldsJson: serializeTemplateTextFields(state.badgeTextFields)
    };
};

export const EventBadgeTemplatePage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {auth} = useContext(AuthContext);
    const {eventId} = useSelectedEventWorkspace();
    const errorLabels = buildUserFacingErrorLabels(t);
    const [side, setSide] = useState<BadgeEditorSide>("front");
    const [state, setState] = useState<BadgeTemplateState>(() => buildBadgeState(null));
    const [downloadingPreview, setDownloadingPreview] = useState(false);
    const query = useEventTemplateSettingsQuery({
        variables: {id: eventId},
        fetchPolicy: "cache-and-network"
    });
    const [saveTemplateSettings, {loading: saving}] = useSaveEventTemplateSettingsMutation();
    const eventBasics = query.data?.eventById;
    const branding = useMemo(() => getEventTemplateBranding(query.data), [query.data]);
    const qrCodeField = state.badgeTextFields.find(isTemplateQrCodeField);
    // Same rule as the server's BadgeSetupValidator: at least one text field, a QR code, and every
    // element inside the A6 canvas. The front artwork is optional (plain white front without it).
    const isInsideCanvas = (field: TemplateTextFieldDefinition) =>
        field.x >= 0 && field.x <= A6_TEMPLATE_CANVAS.width
        && field.y >= 0 && field.y <= A6_TEMPLATE_CANVAS.height;
    const isReadyForPrint = state.badgeTextFields.some(field => !isTemplateQrCodeField(field))
        && !!qrCodeField
        && state.badgeTextFields.every(isInsideCanvas);

    const fileUploadLabels = useMemo(() => ({
        upload: t("upload-file", {defaultValue: "Upload file"}),
        clear: t("clear", {defaultValue: "Clear"}),
        open: t("open", {defaultValue: "Open"}),
        empty: t("file-not-selected", {defaultValue: "No file selected"}),
        success: t("file-uploaded", {defaultValue: "File uploaded"}),
        error: t("error.upload-failed", {defaultValue: "Upload failed"})
    }), [t]);

    useEffect(() => {
        if (!query.loading) {
            setState(buildBadgeState(branding));
        }
    }, [branding, query.loading]);

    const reload = useCallback(async () => {
        const result = await query.refetch();
        setState(buildBadgeState(getEventTemplateBranding(result.data)));
    }, [query]);

    const save = async () => {
        if (!eventBasics) {
            message.error(t("template-event-data-unavailable", {
                defaultValue: "Event data is not loaded yet, so the template cannot be saved. Reload the page and try again."
            }));
            return;
        }

        try {
            await saveTemplateSettings({
                variables: {
                    input: buildEventTemplateSettingsSaveInput(eventBasics, branding, syncLegacyBadgeCoordinates(state))
                }
            });
            message.success(t("badge-template.saved", {defaultValue: "Accreditation template saved"}));
            await reload();
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    const visibleFields = state.badgeTextFields.filter(field => field.side === side);
    const placements: TemplatePlacement[] = visibleFields.map(toTemplatePlacement);

    const updatePlacement = (key: string, point: {x: number; y: number}) => {
        setState(previous => {
            const movedField = previous.badgeTextFields.find(field => field.id === key);

            return {
                ...previous,
                ...(movedField && isTemplateQrCodeField(movedField)
                    ? {badgeQrCodeX: point.x, badgeQrCodeY: point.y}
                    : {}),
                badgeTextFields: previous.badgeTextFields.map(field => field.id === key
                    ? {...field, x: point.x, y: point.y}
                    : field)
            };
        });
    };

    const addTextField = () => {
        const nextId = `${side}-field-${Date.now()}`;
        setState(previous => ({
            ...previous,
            badgeTextFields: [
                ...previous.badgeTextFields,
                {
                    id: nextId,
                    label: t("template-field", {defaultValue: "Field"}),
                    kind: "text",
                    source: "participant",
                    participantField: "fullName",
                    staticText: "",
                    side,
                    x: side === "back" ? 180 : 92,
                    y: side === "back" ? 180 : 218
                }
            ]
        }));
    };

    const updateTextField = (id: string, changes: Partial<TemplateTextFieldDefinition>) => {
        setState(previous => ({
            ...previous,
            badgeTextFields: previous.badgeTextFields.map(field => field.id === id ? {...field, ...changes} : field)
        }));
    };

    const removeTextField = (id: string) => {
        setState(previous => ({
            ...previous,
            badgeTextFields: previous.badgeTextFields.filter(field =>
                field.id !== id || isTemplateQrCodeField(field))
        }));
    };

    const downloadPreview = async () => {
        setDownloadingPreview(true);
        try {
            await downloadBadgeTemplatePreview(eventId, {
                frontTemplateUrl: state.badgeTemplateUrl,
                backTemplateUrl: state.badgeBackTemplateUrl,
                textFields: state.badgeTextFields,
                qrCodeX: qrCodeField?.x ?? state.badgeQrCodeX,
                qrCodeY: qrCodeField?.y ?? state.badgeQrCodeY
            }, auth.token);
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setDownloadingPreview(false);
        }
    };

    const fieldListLabels = {
        add: t("template-field-add", {defaultValue: "Add field"}),
        expand: t("template-field-expand", {defaultValue: "Edit parameters"}),
        collapse: t("template-field-collapse", {defaultValue: "Hide parameters"}),
        backgroundColor: t("template-field-background-color", {defaultValue: "Background"}),
        backgroundOpacity: t("template-field-background-opacity", {defaultValue: "Background opacity"}),
        borderColor: t("template-field-border-color", {defaultValue: "Border"}),
        borderOpacity: t("template-field-border-opacity", {defaultValue: "Border opacity"}),
        borderRadius: t("template-field-border-radius", {defaultValue: "Radius"}),
        borderWidth: t("template-field-border-width", {defaultValue: "Border width"}),
        color: t("template-field-color", {defaultValue: "Text color"}),
        fieldLabel: t("template-field-label", {defaultValue: "Label"}),
        fontFamily: t("template-field-font-family", {defaultValue: "Font"}),
        fontSize: t("template-field-font-size", {defaultValue: "Font size"}),
        fontStyle: t("template-field-font-style", {defaultValue: "Style"}),
        fontStyleItalic: t("template-field-font-style-italic", {defaultValue: "Italic"}),
        fontStyleNormal: t("template-field-font-style-normal", {defaultValue: "Normal"}),
        fontWeight: t("template-field-font-weight", {defaultValue: "Weight"}),
        participantField: t("template-field-participant-field", {defaultValue: "Participant field"}),
        positionX: t("template-field-position-x", {defaultValue: "X position"}),
        positionY: t("template-field-position-y", {defaultValue: "Y position"}),
        remove: t("template-field-remove", {defaultValue: "Remove field"}),
        shadow: t("template-field-shadow", {defaultValue: "Shadow"}),
        source: t("template-field-source", {defaultValue: "Source"}),
        sourceParticipant: t("template-field-source-participant", {defaultValue: "Participant"}),
        sourceStatic: t("template-field-source-static", {defaultValue: "Static text"}),
        staticText: t("template-field-static-text", {defaultValue: "Static text"}),
        title: t("template-fields", {defaultValue: "Fields"}),
        type: t("template-field-type", {defaultValue: "Type"}),
        typeQr: t("template-field-type-qr", {defaultValue: "QR code"}),
        typeText: t("template-field-type-text", {defaultValue: "Text"}),
        size: t("template-field-size", {defaultValue: "Size"})
    };
    const previewAlt = side === "front"
        ? t("badge-template.front-preview-alt", {defaultValue: "Accreditation front preview"})
        : t("badge-template.back-preview-alt", {defaultValue: "Accreditation back preview"});

    if (query.loading && !eventBasics) {
        return <Spin/>;
    }

    if (query.error) {
        return (
            <Alert
                type="error"
                message={t("badge-template.load-error", {defaultValue: "Accreditation template unavailable"})}
                description={getUserFacingErrorMessage(query.error, t, errorLabels)}
            />
        );
    }

    return (
        <div className="ati-stack">
            <PageHeader
                level={2}
                title={t("badge-template.title", {defaultValue: "Accreditation template"})}
                actions={(
                    <StatusTag tone={isReadyForPrint ? "success" : "warning"}>
                        {isReadyForPrint
                            ? t("badge-template.ready", {defaultValue: "Ready"})
                            : t("badge-template.incomplete", {defaultValue: "Incomplete"})}
                    </StatusTag>
                )}
            />

            <Card
                title={t("badge-template.editor", {defaultValue: "Accreditation visual editor"})}
                extra={(
                    <Space wrap>
                        <Button type="primary" icon={<SaveOutlined/>} loading={saving} disabled={!eventBasics} onClick={save}>
                            {t("save", {defaultValue: "Save"})}
                        </Button>
                    </Space>
                )}
            >
                <div className="ati-stack">
                    <div style={{
                        display: "grid",
                        gap: 16,
                        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))"
                    }}>
                        <div>
                            <Typography.Text strong>
                                {t("badge-template-front-file", {defaultValue: "Front accreditation artwork file"})}
                            </Typography.Text>
                            <FileUploadUrlInput
                                value={state.badgeTemplateUrl}
                                onChange={value => setState(previous => ({
                                    ...previous,
                                    badgeTemplateUrl: value
                                }))}
                                authToken={auth.token}
                                accept=".pdf,application/pdf,image/*"
                                uploadText={fileUploadLabels.upload}
                                clearText={fileUploadLabels.clear}
                                openText={fileUploadLabels.open}
                                emptyText={fileUploadLabels.empty}
                                uploadSuccessText={fileUploadLabels.success}
                                uploadErrorText={fileUploadLabels.error}
                            />
                        </div>
                        <div>
                            <Typography.Text strong>
                                {t("badge-template-back-file", {defaultValue: "Back accreditation artwork file"})}
                            </Typography.Text>
                            <FileUploadUrlInput
                                value={state.badgeBackTemplateUrl}
                                onChange={value => setState(previous => ({
                                    ...previous,
                                    badgeBackTemplateUrl: value
                                }))}
                                authToken={auth.token}
                                accept=".pdf,application/pdf,image/*"
                                uploadText={fileUploadLabels.upload}
                                clearText={fileUploadLabels.clear}
                                openText={fileUploadLabels.open}
                                emptyText={fileUploadLabels.empty}
                                uploadSuccessText={fileUploadLabels.success}
                                uploadErrorText={fileUploadLabels.error}
                            />
                        </div>
                    </div>

                    <Segmented
                        value={side}
                        options={[
                            {label: t("badge-template.front", {defaultValue: "Front"}), value: "front"},
                            {label: t("badge-template.back", {defaultValue: "Back"}), value: "back"}
                        ]}
                        onChange={value => setSide(value as BadgeEditorSide)}
                    />

                    <div className="visual-template-editor-layout">
                        <section
                            aria-label={previewAlt}
                            className="visual-template-editor-preview"
                        >
                            <TemplatePlacementEditor
                                authToken={auth.token}
                                backgroundUrl={side === "front" ? state.badgeTemplateUrl : state.badgeBackTemplateUrl}
                                backgroundAlt={previewAlt}
                                canDownloadPreview
                                downloadText={t("template-preview-download", {defaultValue: "Download preview"})}
                                downloadingPreview={downloadingPreview}
                                emptyText={side === "front"
                                    ? t("badge-template.front-empty", {defaultValue: "Upload front artwork to preview front placement."})
                                    : t("badge-template.back-empty", {defaultValue: "Upload back artwork to preview QR placement."})}
                                logicalWidth={BADGE_CANVAS.width}
                                logicalHeight={BADGE_CANVAS.height}
                                placements={placements}
                                onDownloadPreview={downloadPreview}
                                onPlacementChange={updatePlacement}
                            />
                        </section>

                        <section
                            aria-label={fieldListLabels.title}
                            className="visual-template-editor-fields"
                            style={{overflowX: "auto"}}
                        >
                            <div className="visual-template-editor-fields-content">
                                <TemplateTextFieldList
                                    fields={visibleFields}
                                    labels={fieldListLabels}
                                    logicalWidth={BADGE_CANVAS.width}
                                    logicalHeight={BADGE_CANVAS.height}
                                    showFieldType
                                    onAdd={addTextField}
                                    onUpdate={updateTextField}
                                    onRemove={removeTextField}
                                />
                            </div>
                        </section>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default EventBadgeTemplatePage;
